import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// Toasts do portal: confirmam ações sem interromper (substituem os pop-ups modais do Swal).
// Sucesso/info somem em 5 s; erro fica 9 s. Todos têm botão de fechar e são lidos por leitores de tela.
const ToastContext = createContext(null);

const DURATION = { success: 5000, info: 5000, error: 9000 };
const ICON = { success: "✓", info: "i", error: "!" };

export function ToastProvider({ children, max = 4 }) {
  const [items, setItems] = useState([]);
  const nextId = useRef(1);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind, message, opts = {}) => {
      const id = nextId.current++;
      setItems((list) => [...list, { id, kind, message, title: opts.title }].slice(-max));
      timers.current[id] = setTimeout(() => dismiss(id), opts.duration || DURATION[kind] || 5000);
      return id;
    },
    [dismiss, max]
  );

  useEffect(() => {
    const t = timers.current;
    return () => Object.values(t).forEach(clearTimeout);
  }, []);

  const api = useMemo(
    () => ({
      success: (m, o) => push("success", m, o),
      error: (m, o) => push("error", m, o),
      info: (m, o) => push("info", m, o),
      dismiss,
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="c-toasts" aria-live="polite" aria-relevant="additions">
        {items.map((t) => (
          <div key={t.id} className={`c-toast c-toast--${t.kind}`} role={t.kind === "error" ? "alert" : "status"}>
            <span className="c-toast__icon" aria-hidden="true">{ICON[t.kind]}</span>
            <div className="c-toast__body">
              {t.title && <strong>{t.title}</strong>}
              <span>{t.message}</span>
            </div>
            <button type="button" className="c-toast__close" onClick={() => dismiss(t.id)} aria-label="Fechar aviso">
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const NOOP = { success() {}, error() {}, info() {}, dismiss() {} };

// Fora do provider devolve funções vazias: um componente isolado (teste) nunca quebra.
export default function useToast() {
  return useContext(ToastContext) || NOOP;
}
